import React, { useState, useContext } from "react";
import { filterCols } from "../utils/filterResponse";
import { AuthContext } from "../store/ContextStore";
import { Link } from "react-router-dom";
import { getRawStatName } from "../utils/reverseMapping";

const getSearchedStats = (row: any): string[] => {
  // console.log("Full row object:", row);

  // Define metadata fields that should be excluded
  const metadataFields = [
    "ID",
    "CLASS",
    "POS",
    "DATE",
    "OPP",
    "RES",
    "PLAYER",
    "TEAM",
    "USABLE_PERIODS",
    "STATS_THAT_DO_NOT_MATCH",
    "PLAYER_ID",
    "G_MATCH_ID",
    "TEAM_CODE",
    "OPP CONF",
    "PERIOD_TYPE",
    "PERIOD_NUMBER",
  ];

  // Get only the stats fields (exclude metadata)
  const statsKeys = Object.keys(row).filter(
    (key) => !metadataFields.includes(key)
  );

  // console.log("Stats keys found:", statsKeys);

  return statsKeys;
};

// SVG Sort Icon Component
const SortIcon = ({
  direction,
  isActive,
  color,
  onClick,
}: {
  direction: "up" | "down";
  isActive: boolean;
  color: string;
  onClick: () => void;
}) => (
  <svg
    width="11"
    height="8"
    viewBox="0 0 8 6"
    className="cursor-pointer hover:opacity-80 transition-opacity"
    onClick={onClick}
    style={{ minWidth: "8px" }}
  >
    {direction === "up" ? (
      <path
        d="M4 0L0 6h8L4 0z"
        fill={isActive ? color : "#ccc"}
        className="transition-colors duration-200"
      />
    ) : (
      <path
        d="M4 6L0 0h8L4 6z"
        fill={isActive ? color : "#ccc"}
        className="transition-colors duration-200"
      />
    )}
  </svg>
);

function Table(props: any) {
  const {
    data,
    fullData,
    setSortFields,
    handleSortChange,
    sortFields,
    currentPage = 1,
    itemsPerPage = 10,
  } = props || {};

  // console.log(" Incoming `data` in Table component:", data); // <--- ADD THIS

  const { team, selectedSport } = useContext(AuthContext);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const filteredData = filterCols(data);
  const headers = Object.keys(filteredData[0]).filter(
    (header) =>
      header !== "USABLE_PERIODS" && header !== "STATS_THAT_DO_NOT_MATCH" // Hide this column too
  );

  const rgbaFromHex = (hex: string, opacity: number): string => {
    hex = hex.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Function to determine if row should be highlighted based on USABLE_PERIODS
  // Function to determine if row should be highlighted based on USABLE_PERIODS
  const shouldHighlightRow = (row: any, fullData?: any): boolean => {
    // 1. Only check rows where USABLE_PERIODS = 0
    if (row.USABLE_PERIODS !== 0 || row.PERIOD_TYPE !== "REGULAR") {
      //  console.log(' USABLE_PERIODS !== 0, skipping...');
      return false;
    }
    // console.log("full data", fullData.aql_output.periods);
    // // // 2. NEW CONDITION: Check aql_output conditions
    if (
      fullData?.aql_output?.periods === null &&
      fullData?.aql_output?.stat_period !== "period"
    ) {
      // console.log("condition met")
      // console.log("periods are", fullData?.aql_output?.periods)
      // console.log("stat period is", fullData?.aql_output?.stat_period)
      return false;
    }
    // console.log("entering the 3rd condition..")
    // console.log('USABLE_PERIODS = 0, checking stats...');

    // 3. Get STATS_THAT_DO_NOT_MATCH array

    // 4. Extract searched stats from flattened row
    const searchedStats = getSearchedStats(row);

    // console.log('Searched stats from row:', searchedStats);

    // 5. Convert each stat and check
    // console.log('Checking each stat:');
    const shouldHighlight = searchedStats.some((statLabel) => {
      const rawStatKey = getRawStatName(statLabel); // handles casing internally
      const exists = (row.STATS_THAT_DO_NOT_MATCH ?? []).includes(rawStatKey);

      // console.log(`Checking stat: "${statLabel}" → raw: "${rawStatKey}" → exists: ${exists}`);
      return exists;
    });

    return shouldHighlight;
  };

  // Function to handle mouse move and update tooltip position
  const handleMouseMove = (e: React.MouseEvent, rowIndex: number) => {
    if (shouldHighlightRow(filteredData[rowIndex], fullData)) {
      setTooltipPosition({ x: e.clientX + 10, y: e.clientY - 10 });
      setHoveredRow(rowIndex);
    }
  };

  // Function to handle mouse leave
  const handleMouseLeave = () => {
    setHoveredRow(null);
    setTooltipPosition(null);
  };

  // Function to get row background color
  const getRowBackgroundColor = (row: any, rowIndex: number): string => {
    const isSelected = selectedRows.includes(rowIndex);
    const shouldHighlight = shouldHighlightRow(row, fullData);

    if (isSelected) {
      return rgbaFromHex(team.colorCode, 0.2);
    } else if (shouldHighlight) {
      // Graceful highlighting for USABLE_PERIODS = 0
      return rgbaFromHex("#d32626ff", 0.1); // Light red highlight
    }

    return "";
  };

  const isAllSelected = selectedRows.length === data.length;
  const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

  const handleRowSelect = (rowIndex: number) => {
    setSelectedRows((prev) =>
      prev.includes(rowIndex)
        ? prev.filter((id) => id !== rowIndex)
        : [...prev, rowIndex]
    );
  };

  const handleHeaderSelect = () => {
    if (isAllSelected || isSomeSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredData.map((_: any, index: number) => index));
    }
  };

  const toggleAccordion = (rowIndex: number) => {
    setExpandedRow((prev) => (prev === rowIndex ? null : rowIndex));
  };

  const getRowData = (header: string, row: any, rowIndex?: number) => {
  if (header === "ID") {
    return (currentPage - 1) * itemsPerPage + (rowIndex || 0) + 1;
  }
  
  if (header === "RES") {
    // Check if RES value is null or undefined
    if (row[header] === null || row[header] === undefined) {
      return "-";
    }
    return row[header] == 0 ? "L" : "W";
  }

  if (header === "PLAYER") {
    const playerId = row["PLAYER_ID"];
    if (!playerId) return row[header] || "-";

    return (
      <Link
        to={`/playerdashboard/${selectedSport}/${playerId}`}
        className="text-blue-600 hover:underline"
      >
        {row[header] || "-"}
      </Link>
    );
  }

  if(header === "TEAM") {
    const teamCode = row["TEAM_CODE"];
    if (!teamCode) return row[header] || "-";
    return (
      <Link
        to={`/teamdashboard/${selectedSport}/${teamCode}`}
        className="text-blue-600 hover:underline"
      >
        {row[header] || "-"}
      </Link>
    );
  }

  if (header === "DATE") {
    const gMatchID = row["G_MATCH_ID"];
    const teamCode = row["TEAM_CODE"];

    if (!gMatchID || row[header] === "None")
      return row[header] && row[header] !== "None" ? row[header] : "-";

    return (
      <Link
        to={`/gamedashboard/${selectedSport}/${gMatchID}/${teamCode}`}
        className="text-blue-600 hover:underline"
      >
        {row[header] || "-"}
      </Link>
    );
  }

  // FIX: Convert to number first, then check if it's a decimal
  const value = row[header];
  const numValue = Number(value);
  
  if (!isNaN(numValue) && typeof value !== 'boolean' && value !== null && value !== '') {
    // Check if it has decimals
    if (numValue.toString().includes(".")) {
      const decimalPart = numValue.toString().split(".")[1];
      if (decimalPart && decimalPart.length > 2) {
        return parseFloat(numValue.toFixed(2));
      }
    }
    return numValue;
  }

  if (!row[header]) {
    return "-";
  }

  return row[header];
};

  const handleSorting = (header: string, value: string) => {
    let newSort: any;

    // If clicking the same column with same direction, clear the sort
    if (sortFields?.sortBy === header && sortFields?.sortOrder === value) {
      newSort = {
        sortBy: null,
        sortOrder: null,
      };
    } else {
      newSort = {
        sortBy: header,
        sortOrder: value,
      };
    }

    // Update state and trigger sort change immediately
    setSortFields(newSort);
    handleSortChange(newSort);
  };

  // Helper function to check if a sort direction is active
  const isSortActive = (header: string, direction: "ASC" | "DESC"): boolean => {
    return sortFields?.sortBy === header && sortFields?.sortOrder === direction;
  };

  return (
    <div className="p-4 bg-white shadow-md rounded-md overflow-hidden relative">
      <div className="overflow-x-auto">
        <div className="overflow-y-auto max-h-[600px]">
          {/* Adjust max-height as needed */}
          <table className="w-full border-collapse table-auto text-left">
            {/* Table Header */}
            <thead className="font-primary font-semibold text-base text-[#555555]">
              <tr className="border-b">
                <th></th>
                {headers.map((header: any, index: number) => {
                  if (header === "PLAYER_ID") return null;
                  if (header === "G_MATCH_ID") return null;
                  if (header === "TEAM_CODE") return null;

                  return (
                    <th
                      key={index}
                      className="min-w-[max-content] p-4 whitespace-nowrap overflow-hidden text-ellipsis"
                    >
                      <div className="flex items-center gap-[0.25rem]">
                        <span className="font-semibold">
                          {header.replace(/_/g, " ").toUpperCase()}
                        </span>
                        {/* Only show sort icons if header is not 'ID' */}
                        {header !== "ID" && (
                          <div className="flex flex-col items-center gap-[0.15rem]">
                            <SortIcon
                              direction="up"
                              isActive={isSortActive(header, "ASC")}
                              color={team.colorCode}
                              onClick={() => handleSorting(header, "ASC")}
                            />
                            <SortIcon
                              direction="down"
                              isActive={isSortActive(header, "DESC")}
                              color={team.colorCode}
                              onClick={() => handleSorting(header, "DESC")}
                            />
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody className="text-gray-600 text-sm font-primary font-medium">
              {filteredData.map((row: any, rowIndex: number) => (
                <React.Fragment key={rowIndex}>
                  <tr
                    className={`cursor-pointer ${
                      filteredData.length - 1 === rowIndex ? "" : "border-b"
                    }`}
                    style={{
                      backgroundColor: getRowBackgroundColor(row, rowIndex),
                    }}
                    onMouseMove={(e) => handleMouseMove(e, rowIndex)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <td className="">
                      <span
                        className={`inline-block h-4 transform transition-transform duration-200 ${
                          expandedRow === rowIndex ? "rotate-90" : ""
                        }`}
                        onClick={() => toggleAccordion(rowIndex)}
                      ></span>
                    </td>

                    {headers.map((header: any, cellIndex: number) => {
                      if (header === "PLAYER_ID") return null;
                      if (header === "G_MATCH_ID") return null;
                      if (header === "TEAM_CODE") return null;

                      return (
                        <td
                          key={cellIndex}
                          className={`p-4 whitespace-nowrap overflow-hidden text-ellipsis 
            ${header === "RES" && row[header] === "0" ? "text-[#CA2A2A]" : ""}`}
                          style={{
                            color: header === "NAME" ? team.colorCode : "",
                          }}
                        >
                          {getRowData(header, row, rowIndex)}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Custom Tooltip that follows cursor */}
      {hoveredRow !== null &&
        tooltipPosition &&
        shouldHighlightRow(filteredData[hoveredRow], fullData) && (
          <div
            className="fixed bg-gray-800 text-white text-sm px-3 py-2 rounded-md shadow-lg pointer-events-none z-[9999]"
            style={{
              left: `${tooltipPosition.x}px`,
              top: `${tooltipPosition.y}px`,
              maxWidth: "250px",
            }}
          >
            The stats for this player might be incorrect
          </div>
        )}
    </div>
  );
}

export default Table;